/**
 * EnvironnementTab — grille de cartes alimentées par des APIs publiques.
 *
 * Structure :
 *   - Bandeau synthèse 4 KPIs en tête (école, commerces, DPE, fibre).
 *   - Puis 4 sections thématiques, chacune préfixée d'un sur-titre
 *     discret (`font-mono uppercase text-ink-500`) + filet horizontal :
 *       1. Contraintes      → ABF/MH, bruit aéronautique (go/no-go)
 *       2. Quotidien        → écoles, commerces, transports
 *       3. Connectivité     → fibre ARCEP
 *       4. Marché local     → DPE voisins, Filosofi, historique prix
 *
 * Rationale du sectioning (cf. docs/ux-audit-fiche-2026-04-23.md §2) :
 * la page était trop dense en lecture linéaire. Les sections donnent un
 * repère narratif : on lit d'abord ce qui peut **bloquer** (contraintes),
 * puis ce qui entoure la vie de tous les jours, puis les signaux de
 * marché. `EnvSynthesisBar` reste hors section — c'est le lead du tab.
 *
 * Chaque carte lit son propre cache Firestore
 * `/users/{uid}/fiches/{ficheId}/env/{blockId}` via le hook `useEnvBlock`
 * (cf. `src/lib/env/shared.ts`) ; le bandeau synthèse réutilise les mêmes
 * hooks que les cartes et la dedup in-flight garantit qu'on ne fait chaque
 * fetch upstream qu'une fois.
 *
 * Phases ultérieures (cf. docs/fiche-v2-plan.md § Phase 2 Bonus) :
 *   - Loyers de référence (OLAP) — viendra compléter la section "Marché local"
 *   - Permis Sitadel2
 */

import type { ReactNode } from "react";
import type { FicheDoc } from "../lib/fiches";
import { EnvSynthesisBar } from "./env/EnvSynthesisBar";
import { AbfCard } from "./env/AbfCard";
import { BruitCard } from "./env/BruitCard";
import { EcolesCard } from "./env/EcolesCard";
import { CommercesCard } from "./env/CommercesCard";
import { DpeCard } from "./env/DpeCard";
import { TransportsCard } from "./env/TransportsCard";
import { ArcepCard } from "./env/ArcepCard";
import { FilosofiCard } from "./env/FilosofiCard";
import { PriceHistoryCard } from "./env/PriceHistoryCard";

export function EnvironnementTab({ fiche }: { fiche: FicheDoc }) {
  return (
    <div className="py-4 space-y-8">
      <EnvSynthesisBar fiche={fiche} />

      {/* Contraintes — go/no-go structurel. En tête parce que l'ABF et le
          bruit PEB peuvent changer le calcul d'un projet dès la première
          lecture. Quand 0 servitude, les bandeaux verts calmes se lisent
          en 2 s. */}
      <EnvSection title="Contraintes">
        <AbfCard fiche={fiche} />
        <BruitCard fiche={fiche} />
      </EnvSection>

      {/* Quotidien — vie de tous les jours autour du bien.
          Grille 2 cols pour Écoles/Commerces (listes ~45 ch dont les noms
          se tronquent dès qu'on descend sous cette largeur). Transports
          en pleine largeur dessous : la liste de stations par mode de
          transport respire mieux étirée. */}
      <EnvSection title="Quotidien">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <EcolesCard fiche={fiche} />
          <CommercesCard fiche={fiche} />
          <div className="md:col-span-2">
            <TransportsCard fiche={fiche} />
          </div>
        </div>
      </EnvSection>

      {/* Connectivité — internet haut débit ARCEP. Pleine largeur car la
          barre empilée 4 segments (fibre/coax/cuivre/zone blanche) gagne
          énormément à être étirée, et la légende 4 items tient sur 2×2
          naturellement. */}
      <EnvSection title="Connectivité">
        <ArcepCard fiche={fiche} />
      </EnvSection>

      {/* Marché local — signaux macro du quartier.
          DPE voisins (état du parc rénové) → Filosofi (profil socio-démo
          du carreau 200 m) → historique de prix m² sur 10 ans. L'ordre
          va du qualitatif (parc bâti, population) au chiffré (prix). */}
      <EnvSection title="Marché local">
        <DpeCard fiche={fiche} />
        <FilosofiCard fiche={fiche} />
        <PriceHistoryCard fiche={fiche} />
      </EnvSection>
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────────
// Label mono uppercase + filet horizontal qui file jusqu'au bord droit.
// Pattern éditorial classique : discret mais il donne un repère scanable
// sans introduire une 2e hiérarchie visuelle (les cartes restent les
// objets principaux).

function EnvSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500 font-medium shrink-0">
          {title}
        </h2>
        <div className="flex-1 h-px bg-ink-200/60" />
      </div>
      {children}
    </section>
  );
}
